import { useTranslation } from 'react-i18next';
import { X, Tag, Calendar, CheckCircle, XCircle, Clock, Edit } from 'lucide-react';
import type { Promotion } from '../../../api/queries/promotion.queries';

interface PromotionDetailModalProps {
  promotion: Promotion;
  onClose: () => void;
  onEdit: () => void;
}

export default function PromotionDetailModal({ promotion, onClose, onEdit }: PromotionDetailModalProps) {
  const { t, i18n } = useTranslation();

  const getLangValue = (multiLang: any) => {
    return multiLang?.[i18n.language as 'en' | 'th'] || multiLang?.en || '';
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString(i18n.language === 'th' ? 'th-TH' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'discount':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'bundle':
        return 'text-purple-600 bg-purple-50 border-purple-200';
      case 'buy_x_get_y':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'seasonal':
        return 'text-orange-600 bg-orange-50 border-orange-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getDiscountLabel = () => {
    const { discount } = promotion;
    switch (discount.type) {
      case 'percentage':
        return `${discount.value}% off`;
      case 'fixed_amount':
        return `฿${discount.value} off`;
      case 'fixed_price':
        return `฿${discount.value} fixed price`;
      case 'buy_x_get_y':
        return 'Buy X Get Y';
      default:
        return discount.type;
    }
  };

  const isExpired = new Date(promotion.endDate) < new Date();
  const isUpcoming = new Date(promotion.startDate) > new Date();
  const isCurrentlyActive = promotion.isActive && !isExpired && !isUpcoming;

  const getConditionLabel = (condition: any) => {
    switch (condition.type) {
      case 'min_quantity':
        return `${t('promotions.minQuantity')}: ${condition.value}`;
      case 'min_amount':
        return `${t('promotions.minAmount')}: ฿${condition.value}`;
      case 'day_of_week':
        const days = condition.value || [];
        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        return `${t('promotions.dayOfWeek')}: ${days.map((d: number) => dayNames[d]).join(', ')}`;
      case 'time_range':
        return `${t('promotions.timeRange')}: ${condition.value?.start} - ${condition.value?.end}`;
      default:
        return condition.type;
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="relative w-full max-w-2xl max-h-[90vh] overflow-hidden rounded-lg bg-card flex flex-col">
        {/* Header */}
        <div className="border-b p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Tag className="h-5 w-5 text-primary" />
            <div>
              <h2 className="text-xl font-bold">{getLangValue(promotion.name)}</h2>
              <p className="text-sm text-muted-foreground">{promotion.couponCode && `Code: ${promotion.couponCode}`}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-2 py-1 rounded border text-xs ${getTypeColor(promotion.type)}`}>
              {t(`promotions.${promotion.type}`)}
            </span>
            {isCurrentlyActive && (
              <span className="px-2 py-1 rounded bg-green-100 text-green-700 text-xs flex items-center gap-1">
                <CheckCircle className="h-3 w-3" />
                {t('promotions.active')}
              </span>
            )}
            {isExpired && (
              <span className="px-2 py-1 rounded bg-gray-100 text-gray-600 text-xs">
                {t('promotions.expired')}
              </span>
            )}
            {isUpcoming && (
              <span className="px-2 py-1 rounded bg-blue-100 text-blue-600 text-xs flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {t('promotions.upcoming')}
              </span>
            )}
            <button onClick={onClose} className="p-2 hover:bg-secondary rounded ml-2">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Description */}
            {promotion.description && (
              <div>
                <p className="text-muted-foreground">{getLangValue(promotion.description)}</p>
              </div>
            )}

            {/* Discount Info */}
            <div className="p-4 rounded-lg bg-primary/10 border border-primary/20">
              <div className="flex items-center gap-3">
                <Tag className="h-8 w-8 text-primary" />
                <div>
                  <p className="text-2xl font-bold text-primary">{getDiscountLabel()}</p>
                  {promotion.discount.maxDiscount && (
                    <p className="text-sm text-muted-foreground">
                      {t('promotions.maxDiscount')}: ฿{promotion.discount.maxDiscount}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Date Range */}
            <div className="flex items-center gap-4 p-4 rounded-lg bg-secondary/50">
              <Calendar className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">{t('promotions.validPeriod')}</p>
                <p className="text-sm text-muted-foreground">
                  {formatDate(promotion.startDate)} - {formatDate(promotion.endDate)}
                </p>
              </div>
            </div>

            {/* Conditions */}
            {promotion.conditions && promotion.conditions.length > 0 && (
              <div>
                <h3 className="text-sm font-medium mb-3">{t('promotions.conditions')}</h3>
                <div className="space-y-2">
                  {promotion.conditions.map((condition, index) => (
                    <div key={index} className="flex items-center gap-2 p-2 rounded bg-secondary/50">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="text-sm">{getConditionLabel(condition)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Stats Grid */}
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 rounded-lg border text-center">
                <p className="text-2xl font-bold">{promotion.usageCount}</p>
                <p className="text-xs text-muted-foreground">{t('promotions.timesUsed')}</p>
              </div>
              <div className="p-4 rounded-lg border text-center">
                <p className="text-2xl font-bold">
                  {promotion.usageLimit ? promotion.usageLimit - promotion.usageCount : '∞'}
                </p>
                <p className="text-xs text-muted-foreground">{t('promotions.remaining')}</p>
              </div>
              <div className="p-4 rounded-lg border text-center">
                <p className="text-2xl font-bold">{promotion.priority}</p>
                <p className="text-xs text-muted-foreground">{t('promotions.priority')}</p>
              </div>
            </div>

            {/* Settings */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2">
                {promotion.autoApply ? (
                  <CheckCircle className="h-4 w-4 text-green-500" />
                ) : (
                  <XCircle className="h-4 w-4 text-gray-400" />
                )}
                <span>{t('promotions.autoApply')}</span>
              </div>
              <div className="flex items-center gap-2">
                {promotion.requiresCouponCode ? (
                  <CheckCircle className="h-4 w-4 text-green-500" />
                ) : (
                  <XCircle className="h-4 w-4 text-gray-400" />
                )}
                <span>{t('promotions.requiresCouponCode')}</span>
              </div>
            </div>

            {/* Applicable Info */}
            {(promotion.applicableCategories?.length || promotion.applicableBranches?.length) && (
              <div className="space-y-3">
                {promotion.applicableCategories && promotion.applicableCategories.length > 0 && (
                  <div>
                    <p className="text-sm font-medium mb-1">{t('promotions.applicableCategories')}</p>
                    <p className="text-sm text-muted-foreground">
                      {promotion.applicableCategories.length} {t('promotions.categoriesSelected')}
                    </p>
                  </div>
                )}
                {promotion.applicableBranches && promotion.applicableBranches.length > 0 && (
                  <div>
                    <p className="text-sm font-medium mb-1">{t('promotions.applicableBranches')}</p>
                    <p className="text-sm text-muted-foreground">
                      {promotion.applicableBranches.length} {t('promotions.branchesSelected')}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t p-4 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border hover:bg-secondary transition-colors"
          >
            {t('common.close')}
          </button>
          <button
            onClick={onEdit}
            className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors flex items-center gap-2"
          >
            <Edit className="h-4 w-4" />
            {t('common.edit')}
          </button>
        </div>
      </div>
    </div>
  );
}
